import { useState, useEffect, useCallback } from 'react'
import { SKILL_KEYS } from '../config'
import * as api from '../api'
import { handleApiError } from '../utils/errorHandler'
import type { SkillKey, HistoryItem, Conversation } from '../types'

interface UseHistoryOptions {
  activeSkill: SkillKey
  userId: string
}

interface UseHistoryReturn {
  historyList: HistoryItem[]
  activeHistory: string | null
  setActiveHistory: (id: string | null) => void
  loadHistory: (skill: SkillKey) => Promise<void>
  addHistoryItem: (item: HistoryItem) => void
  removeHistoryItem: (id: string) => Promise<void>
  clearHistory: () => void
}

/**
 * 历史记录相关逻辑的自定义 Hook
 */
export const useHistory = ({ activeSkill, userId }: UseHistoryOptions): UseHistoryReturn => {
  // 存储所有技能的历史记录
  const [allHistory, setAllHistory] = useState<Map<SkillKey, HistoryItem[]>>(new Map())
  const [activeHistory, setActiveHistory] = useState<string | null>(null)

  // 当前技能的历史列表
  const historyList = allHistory.get(activeSkill) || []

  /**
   * 从 API 加载指定技能的会话列表
   */
  const loadHistory = useCallback(async (skill: SkillKey) => {
    try {
      const response = await api.getConversations(skill, userId)
      // 兼容两种格式：直接返回数组 或 { data: 数组 } 结构
      const conversations = Array.isArray(response) ? response : (response?.data || [])
      const historyItems: HistoryItem[] = conversations.map((conv: Conversation) => ({
        id: conv.id,
        title: conv.name,
        skill
      }))
      setAllHistory(prev => {
        const newMap = new Map(prev)
        newMap.set(skill, historyItems)
        return newMap
      })
    } catch (error) {
      console.error('[loadHistory error]', error)
    }
  }, [userId])

  /**
   * 添加历史记录项
   */
  const addHistoryItem = useCallback((item: HistoryItem) => {
    setAllHistory(prev => {
      const newMap = new Map(prev)
      const existingList = newMap.get(item.skill) || []
      // 检查是否已存在
      const exists = existingList.some(h => h.id === item.id)
      let newList: HistoryItem[]
      if (exists) {
        // 更新现有项，移到最前面
        newList = [item, ...existingList.filter(h => h.id !== item.id)]
      } else {
        // 添加到开头
        newList = [item, ...existingList]
      }
      newMap.set(item.skill, newList)
      return newMap
    })
  }, [])

  /**
  * 删除历史记录项
  */
 const removeHistoryItem = useCallback(async (id: string) => {
    // 使用函数式更新，避免依赖 allHistory
    let targetSkill: SkillKey | null = null
    let originalList: HistoryItem[] = []

    // 先从 UI 移除，同时在回调中找到目标技能
    setAllHistory(prev => {
      for (const [skill, items] of prev) {
        const found = items.find(item => item.id === id)
        if (found) {
          targetSkill = skill
          originalList = items
          const newMap = new Map(prev)
          newMap.set(skill, items.filter(item => item.id !== id))
          return newMap
        }
      }
      return prev
    })

    if (!targetSkill) return

    // 如果正在查看该会话，清除当前状态
    if (activeHistory === id) {
      setActiveHistory(null)
    }

    // 调用 API 删除
    try {
      await api.deleteConversation(targetSkill, id, userId)
    } catch (error) {
      handleApiError(error, '删除会话失败')
      // 回滚
      setAllHistory(prev => {
        const newMap = new Map(prev)
        newMap.set(targetSkill!, originalList)
        return newMap
      })
    }
  }, [activeHistory, userId])

  /**
   * 清除所有历史记录
   */
  const clearHistory = useCallback(() => {
    setAllHistory(new Map())
    setActiveHistory(null)
  }, [])

  /**
   * 初始化时加载所有技能的历史记录
   * 当 userId 变化时重新加载
   */
  useEffect(() => {
    if (!userId) return

    const skills: SkillKey[] = SKILL_KEYS

    // 并行加载三个技能的历史记录
    const loadAllHistory = async () => {
      try {
        const results = await Promise.all(
          skills.map(skill => api.getConversations(skill, userId))
        )

        setAllHistory(prev => {
          const newMap = new Map(prev)
          skills.forEach((skill, index) => {
            // API 返回格式: { data: Conversation[], has_more: boolean, limit: number }
            const response = results[index]
            // 兼容两种格式：直接返回数组 或 { data: 数组 } 结构
            const conversations = Array.isArray(response) ? response : (response?.data || [])
            const historyItems: HistoryItem[] = conversations.map((conv: { id: string; name: string }) => ({
              id: conv.id,
              title: conv.name,
              skill
            }))
            newMap.set(skill, historyItems)
          })
          return newMap
        })
      } catch (error) {
        console.error('[loadAllHistory error]', error)
        // 出错时清空历史记录，避免页面崩溃
        setAllHistory(new Map())
      }
    }

    loadAllHistory()
  }, [userId]) // 当 userId 变化时重新加载

  return {
    historyList,
    activeHistory,
    setActiveHistory,
    loadHistory,
    addHistoryItem,
    removeHistoryItem,
    clearHistory
  }
}
